import {
  IonButton,
  IonIcon,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonText
} from '@ionic/react';
import React, { KeyboardEvent, useState } from 'react';

import ButtonContainer from '@/components/common/buttonContainer/ButtonContainer';
import Spacer from '@/components/common/spacer/Spacer';
import { useLanguage } from '@/contexts/LanguageContext';
import { useScheduleFlow } from '@/contexts/ScheduleFlowContext';
import useToast from '@/hooks/useToast';
import {
  GENDER_OPTIONS,
  MINIMUM_AGE_IN_WORDS,
  MINIMUM_AGE_IN_YEARS,
  MOBILE_NUMBER_LENGTH
} from '@/utils/constants';
import { arrowForwardCircleOutline } from 'ionicons/icons';
import moment from 'moment';

import './SignUp.css';

const SignUp: React.FC = () => {
  const { t } = useLanguage();
  const fields = {
    firstName: 'firstName',
    lastName: 'lastName',
    mobileNumber: 'mobileNumber',
    gender: 'gender',
    birthday: 'birthday'
  };

  const {
    sendOtp: onSignUp,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    mobileNumber,
    setMobileNumber,
    gender,
    setGender,
    birthday,
    setBirthday,
    selectedDentist
  } = useScheduleFlow();
  const displayToast = useToast();

  const [errors, setErrors] = useState<string[]>([]);

  const copy = {
    subHeader: t('schedule.signUp.subHeader'),
    header: (
      <>
        {t('schedule.signUp.header.fill')}{' '}
        <IonText color="primary">
          {t('schedule.signUp.header.personal')}
        </IonText>{' '}
        {t('schedule.signUp.header.information')}
      </>
    ),
    message: t('schedule.signUp.message'),
    buttonText: t('schedule.signUp.buttonText'),
    countryCode: t('schedule.signUp.countryCode'),
    genders: {
      male: t('schedule.signUp.genders.male'),
      female: t('schedule.signUp.genders.female'),
      other: t('schedule.signUp.genders.other')
    },
    otpConsent: t('schedule.signUp.otpConsent'),
    form: {
      firstName: {
        label: t('schedule.signUp.form.firstName.label'),
        placeholder: t('schedule.signUp.form.firstName.placeholder')
      },
      lastName: {
        label: t('schedule.signUp.form.lastName.label'),
        placeholder: t('schedule.signUp.form.lastName.placeholder')
      },
      mobileNumber: {
        label: t('schedule.signUp.form.mobileNumber.label'),
        placeholder: t('schedule.signUp.form.mobileNumber.placeholder')
      },
      birthday: {
        label: t('schedule.signUp.form.birthday.label')
      },
      gender: {
        label: t('schedule.signUp.form.gender.label'),
        placeholder: t('schedule.signUp.form.gender.placeholder')
      }
    },
    fields: {
      firstName: t('schedule.signUp.fields.firstName'),
      lastName: t('schedule.signUp.fields.lastName'),
      mobileNumber: t('schedule.signUp.fields.mobileNumber'),
      gender: t('schedule.signUp.fields.gender'),
      birthday: t('schedule.signUp.fields.birthday')
    }
  };

  const onInput = (event: CustomEvent, field: string) => {
    const value = event.detail.value;
    setErrors((prevErrors) => prevErrors.filter((error) => error !== field));

    switch (field) {
      case fields.firstName:
        setFirstName(value);
        break;
      case fields.lastName:
        setLastName(value);
        break;
      case fields.mobileNumber:
        if (value.startsWith('0')) {
          setMobileNumber('');
          displayToast({
            message: t('schedule.signUp.errors.mobileStartsWithZero'),
            duration: 5000,
            position: 'top'
          });
        } else {
          setMobileNumber(value);
        }
        break;
      case fields.gender:
        setGender(value);
        break;
      case fields.birthday:
        setBirthday(value);
        break;
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onNext();
    }
  };

  const validateFields = () => {
    const newErrors: string[] = [];

    if (!firstName) newErrors.push(fields.firstName);
    if (!lastName) newErrors.push(fields.lastName);
    if (!mobileNumber) newErrors.push(fields.mobileNumber);
    if (!gender) newErrors.push(fields.gender);
    if (!birthday) newErrors.push(fields.birthday);

    if (newErrors.length > 0) {
      setErrors(newErrors);
      displayToast({
        message: t('schedule.signUp.errors.allFields'),
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
      return false;
    }

    if (
      mobileNumber?.length !== MOBILE_NUMBER_LENGTH ||
      !/^\d+$/.test(mobileNumber) ||
      mobileNumber.startsWith('0')
    ) {
      newErrors.push(fields.mobileNumber);
      setErrors(newErrors);
      displayToast({
        message: t('schedule.signUp.errors.invalidMobile'),
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
      return false;
    }

    if (
      !moment(birthday).isValid() ||
      !moment(birthday).add(MINIMUM_AGE_IN_YEARS, 'years').isBefore(moment()) ||
      moment(birthday).isAfter(moment())
    ) {
      newErrors.push(fields.birthday);
      setErrors(newErrors);
      displayToast({
        message: t('schedule.signUp.errors.invalidBirthday', {
          minimumAge: MINIMUM_AGE_IN_WORDS
        }),
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
      return false;
    }

    setErrors([]);
    return true;
  };

  const onNext = async () => {
    if (validateFields()) {
      onSignUp();
    }
  };

  return (
    <div id="signUp">
      <Spacer top={32} bottom={24}>
        <h4 id="subHeader">{copy.subHeader}</h4>
        <h1 id="header">{copy.header}</h1>
        <p id="message">{copy.message}</p>
      </Spacer>
      <div id="form">
        <div
          className={`input ${errors.includes(fields.firstName) ? 'error' : ''}`}
        >
          <IonInput
            label={copy.form.firstName.label}
            labelPlacement="stacked"
            value={firstName}
            placeholder={copy.form.firstName.placeholder}
            onIonInput={(event: CustomEvent) =>
              onInput(event, fields.firstName)
            }
            onKeyDown={onKeyDown}
          />
        </div>
        <div
          className={`input ${errors.includes(fields.lastName) ? 'error' : ''}`}
        >
          <IonInput
            label={copy.form.lastName.label}
            labelPlacement="stacked"
            value={lastName}
            placeholder={copy.form.lastName.placeholder}
            onIonInput={(event: CustomEvent) => onInput(event, fields.lastName)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div
          className={`input ${errors.includes(fields.mobileNumber) ? 'error' : ''}`}
        >
          <IonInput
            type="tel"
            label={copy.form.mobileNumber.label}
            labelPlacement="stacked"
            value={mobileNumber}
            placeholder={copy.form.mobileNumber.placeholder}
            onIonInput={(event: CustomEvent) =>
              onInput(event, fields.mobileNumber)
            }
            onKeyDown={onKeyDown}
            maxlength={10}
          >
            <div slot="start">{copy.countryCode}</div>
          </IonInput>
        </div>
        <div
          className={`input ${errors.includes(fields.birthday) ? 'error' : ''}`}
        >
          <IonInput
            label={copy.form.birthday.label}
            labelPlacement="stacked"
            type="date"
            value={birthday}
            onIonInput={(event: CustomEvent) => onInput(event, fields.birthday)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div
          className={`input ${errors.includes(fields.gender) ? 'error' : ''}`}
        >
          <IonSelect
            label={copy.form.gender.label}
            labelPlacement="stacked"
            placeholder={copy.form.gender.placeholder}
            value={gender}
            onIonChange={(event: CustomEvent) => onInput(event, fields.gender)}
            onKeyDown={onKeyDown}
          >
            <IonSelectOption value={GENDER_OPTIONS.MALE}>
              {copy.genders.male}
            </IonSelectOption>
            <IonSelectOption value={GENDER_OPTIONS.FEMALE}>
              {copy.genders.female}
            </IonSelectOption>
            <IonSelectOption value={GENDER_OPTIONS.OTHER}>
              {copy.genders.other}
            </IonSelectOption>
          </IonSelect>
        </div>
      </div>

      <Spacer
        bottom={selectedDentist?.patient_verification_enabled ? 142 : 102}
      />
      <ButtonContainer
        translucent={!selectedDentist?.patient_verification_enabled}
      >
        <IonButton
          id="nextButton"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong={true}
        >
          <IonIcon slot="start" icon={arrowForwardCircleOutline}></IonIcon>
          {copy.buttonText}
        </IonButton>
        {selectedDentist?.patient_verification_enabled && (
          <p id="otpConsent">{copy.otpConsent}</p>
        )}
      </ButtonContainer>
    </div>
  );
};

export default SignUp;
